import { NavLink, useLocation, useParams } from 'react-router';
import { Classes } from '@blueprintjs/core';
import { type UsageTab } from '@gonfalon/billing/types';
import { getUsageDisplayName } from '@gonfalon/billing/utils';
import cx from 'clsx';

import { capitalize } from '../utils';

import { CopyToClipboard } from './CopyToClipboard';

const bsonId = /^[a-f\d]{24}$/i;

const getUsageDisplayNameSafe = (metricType: string): string => {
  try {
    return getUsageDisplayName(metricType as UsageTab, 'longTitleName');
  } catch (_error) {
    return capitalize(metricType.replace(/-/g, ' '));
  }
};

const crumbName = (url: string, metricType?: string) => {
  if (!url) {
    return '';
  }

  const pathParts = url.replace(/\/$/g, '').split('/');
  const path = pathParts[pathParts.length - 1];

  // Handle usage details pages or metric type paths
  if (metricType && (url.includes('/details/') || path === metricType)) {
    return getUsageDisplayNameSafe(metricType);
  }

  // Special cases for specific paths
  const specialCases: Record<string, string> = {
    'provisioning-plan-review': 'Account Provisioning',
    'plan-usage': 'Plan usage',
    'diagnostic-usage': 'Diagnostic usage',
  };

  if (specialCases[path]) {
    return specialCases[path];
  }

  // Return BSON ID as-is or capitalize regular paths
  return bsonId.test(path) ? path : capitalize(path);
};

const Breadcrumb = ({ path, metricType }: { path: string; metricType?: string }) => {
  const location = useLocation();
  const isExact = location.pathname === path;
  const crumbText = crumbName(path, metricType);
  const isBsonId = bsonId.test(crumbText);

  return (
    <li>
      <NavLink
        className={cx(Classes.BREADCRUMB, {
          [Classes.BREADCRUMB_CURRENT]: isExact,
        })}
        to={path}
      >
        {isBsonId ? <CopyToClipboard content={crumbText}>{crumbText}</CopyToClipboard> : crumbText}
      </NavLink>
    </li>
  );
};

const buildBreadcrumbPaths = (pathname: string) => {
  const paths: string[] = [];
  pathname
    .replace(/\/$/g, '')
    .split('/')
    .reduce((prev: string, curr: string) => {
      const sofar = `${prev}/${curr}`;
      paths.push(sofar);
      return sofar;
    });

  // Filter out "details" segments
  return paths.filter((path) => !path.endsWith('/details'));
};

const extractMetricType = (pathname: string, paramsMetricType?: string): string | undefined => {
  if (paramsMetricType) {
    return paramsMetricType;
  }

  const match = pathname.match(/\/(?:plan-usage|diagnostic-usage)\/details\/([^/]+)/);
  return match?.[1];
};

export const Breadcrumbs = () => {
  const location = useLocation();
  const { metricType: paramsMetricType } = useParams<{ metricType?: string }>();

  if (!location) {
    return null;
  }

  const paths = buildBreadcrumbPaths(location.pathname);
  const metricType = extractMetricType(location.pathname, paramsMetricType);

  // For usage details pages, replace the last path with current location for proper metric display
  if (metricType && location.pathname.includes('/details/')) {
    paths[paths.length - 1] = location.pathname;
  }

  return (
    <ul className={Classes.BREADCRUMBS}>
      {paths.map((path) => (
        <Breadcrumb key={path} path={path} metricType={metricType} />
      ))}
    </ul>
  );
};
